// Copyright © 2026 Gian Marco Cappelli — SAPIENS (finsapiens.it). Tutti i diritti riservati. Sviluppato su commissione esclusiva. Vietata la riproduzione senza autorizzazione scritta.
import React, { useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Download } from 'lucide-react';
import jsPDF from 'jspdf';

const LEVEL_NAMES = {
  1: 'Livello Base',
  2: 'Livello Intermedio',
  3: 'Livello Avanzato',
  4: 'Livello Esperto',
};

const CertificatePreview = ({ userName, levelName, issuedAt }) => {
  const dateStr = issuedAt
    ? new Date(issuedAt).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })
    : new Date().toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div
      id="certificate-preview"
      style={{
        background: '#1C1C1A',
        border: '2px solid #C9A84C',
        borderRadius: '12px',
        padding: '40px 48px',
        width: '100%',
        maxWidth: '600px',
        margin: '0 auto',
        fontFamily: 'Inter, system-ui, sans-serif',
        position: 'relative',
      }}
    >
      {/* Decorative corner lines */}
      <div style={{ position: 'absolute', top: 12, left: 12, width: 30, height: 30, borderTop: '2px solid rgba(201,168,76,0.4)', borderLeft: '2px solid rgba(201,168,76,0.4)' }} />
      <div style={{ position: 'absolute', top: 12, right: 12, width: 30, height: 30, borderTop: '2px solid rgba(201,168,76,0.4)', borderRight: '2px solid rgba(201,168,76,0.4)' }} />
      <div style={{ position: 'absolute', bottom: 12, left: 12, width: 30, height: 30, borderBottom: '2px solid rgba(201,168,76,0.4)', borderLeft: '2px solid rgba(201,168,76,0.4)' }} />
      <div style={{ position: 'absolute', bottom: 12, right: 12, width: 30, height: 30, borderBottom: '2px solid rgba(201,168,76,0.4)', borderRight: '2px solid rgba(201,168,76,0.4)' }} />

      {/* Brand */}
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#C9A84C', display: 'inline-block' }} />
          <span style={{ color: '#E8E8E4', fontSize: 18, fontWeight: 700, letterSpacing: '0.2em' }}>SAPIENS</span>
        </div>
        <p style={{ color: '#888780', fontSize: 11, marginTop: 4, letterSpacing: '0.1em' }}>EDUCAZIONE FINANZIARIA</p>
      </div>

      {/* Divider */}
      <div style={{ borderTop: '1px solid rgba(201,168,76,0.25)', marginBottom: 28 }} />

      {/* Title */}
      <p style={{ textAlign: 'center', color: '#888780', fontSize: 13, letterSpacing: '0.15em', marginBottom: 12, textTransform: 'uppercase' }}>
        Certificato di Completamento
      </p>

      {/* This certifies that */}
      <p style={{ textAlign: 'center', color: '#888780', fontSize: 14, marginBottom: 8 }}>
        Si certifica che
      </p>

      {/* Name */}
      <h2 style={{ textAlign: 'center', color: '#C9A84C', fontSize: 28, fontWeight: 700, margin: '0 0 20px' }}>
        {userName || 'Nome Utente'}
      </h2>

      {/* Body */}
      <p style={{ textAlign: 'center', color: '#E8E8E4', fontSize: 14, lineHeight: 1.7, marginBottom: 8 }}>
        ha completato con successo il percorso
      </p>
      <h3 style={{ textAlign: 'center', color: '#3D7A52', fontSize: 22, fontWeight: 700, margin: '0 0 24px' }}>
        {levelName}
      </h3>
      <p style={{ textAlign: 'center', color: '#888780', fontSize: 13, marginBottom: 4 }}>
        sulla piattaforma di educazione finanziaria SAPIENS
      </p>

      {/* Divider */}
      <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', margin: '24px 0' }} />

      {/* Footer row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div>
          <p style={{ color: '#888780', fontSize: 11, marginBottom: 2 }}>Data di emissione</p>
          <p style={{ color: '#E8E8E4', fontSize: 13, fontWeight: 600 }}>{dateStr}</p>
        </div>
        <div style={{ textAlign: 'right' }}>
          <p style={{ color: '#888780', fontSize: 11, marginBottom: 2 }}>Piattaforma</p>
          <p style={{ color: '#C9A84C', fontSize: 12, fontWeight: 600 }}>sapiens.finance</p>
        </div>
      </div>
    </div>
  );
};

const generatePDF = (userName, levelName, issuedAt, verificationCode) => {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });

  const W = 297, H = 210;

  // Background
  doc.setFillColor(28, 28, 26);
  doc.rect(0, 0, W, H, 'F');

  // Gold border
  doc.setDrawColor(201, 168, 76);
  doc.setLineWidth(1.5);
  doc.rect(10, 10, W - 20, H - 20);

  // Inner border
  doc.setDrawColor(201, 168, 76, 0.3);
  doc.setLineWidth(0.5);
  doc.rect(14, 14, W - 28, H - 28);

  // Corner decorations
  const cd = 12;
  [[14, 14], [W - 14, 14], [14, H - 14], [W - 14, H - 14]].forEach(([cx, cy]) => {
    doc.setDrawColor(201, 168, 76);
    doc.setLineWidth(1.5);
    if (cx < W / 2 && cy < H / 2) { doc.line(cx, cy, cx + cd, cy); doc.line(cx, cy, cx, cy + cd); }
    if (cx > W / 2 && cy < H / 2) { doc.line(cx, cy, cx - cd, cy); doc.line(cx, cy, cx, cy + cd); }
    if (cx < W / 2 && cy > H / 2) { doc.line(cx, cy, cx + cd, cy); doc.line(cx, cy, cx, cy - cd); }
    if (cx > W / 2 && cy > H / 2) { doc.line(cx, cy, cx - cd, cy); doc.line(cx, cy, cx, cy - cd); }
  });

  // Brand • SAPIENS
  doc.setTextColor(201, 168, 76);
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.text('• SAPIENS', W / 2, 38, { align: 'center' });

  doc.setTextColor(136, 135, 128);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text('EDUCAZIONE FINANZIARIA', W / 2, 45, { align: 'center' });

  // Divider
  doc.setDrawColor(201, 168, 76);
  doc.setLineWidth(0.4);
  doc.line(60, 52, W - 60, 52);

  // Subtitle
  doc.setTextColor(136, 135, 128);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text('CERTIFICATO DI COMPLETAMENTO', W / 2, 65, { align: 'center' });

  doc.text('Si certifica che', W / 2, 76, { align: 'center' });

  // Name
  doc.setTextColor(201, 168, 76);
  doc.setFontSize(28);
  doc.setFont('helvetica', 'bold');
  doc.text(userName || 'Nome Utente', W / 2, 92, { align: 'center' });

  // Body
  doc.setTextColor(232, 232, 228);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.text('ha completato con successo il percorso', W / 2, 106, { align: 'center' });

  // Level
  doc.setTextColor(61, 122, 82);
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text(levelName, W / 2, 120, { align: 'center' });

  doc.setTextColor(136, 135, 128);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.text('sulla piattaforma di educazione finanziaria SAPIENS', W / 2, 132, { align: 'center' });

  // Divider
  doc.setDrawColor(60, 60, 58);
  doc.setLineWidth(0.3);
  doc.line(40, 148, W - 40, 148);

  // Footer
  const dateStr = issuedAt
    ? new Date(issuedAt).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })
    : new Date().toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' });

  doc.setTextColor(136, 135, 128);
  doc.setFontSize(9);
  doc.text('Data di emissione', 50, 160);
  doc.setTextColor(232, 232, 228);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text(dateStr, 50, 167);

  doc.setTextColor(136, 135, 128);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text('Piattaforma', W - 50, 160, { align: 'right' });
  doc.setTextColor(201, 168, 76);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('finsapiens.it', W - 50, 167, { align: 'right' });

  // Verification code
  if (verificationCode) {
    doc.setTextColor(136, 135, 128);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(`Codice verifica: ${verificationCode}`, W / 2, 190, { align: 'center' });
  }

  // Disclaimer
  doc.setTextColor(80, 80, 78);
  doc.setFontSize(7);
  doc.text('Certificato a scopo educativo — non ha valore legale o professionale', W / 2, 196, { align: 'center' });

  const safeName = (levelName || 'Livello').replace(/[^a-zA-Z0-9]/g, '-');
  doc.save(`SAPIENS_Certificato_${safeName}.pdf`);
};

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CertificateModal = ({ isOpen, onClose, level, levelName, userName, issuedAt, verificationCode, onDownload }) => {
  const name = LEVEL_NAMES[level] || levelName || `Livello ${level}`;
  const [downloading, setDownloading] = React.useState(false);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      // Try backend PDF first (ReportLab — better quality)
      const token = localStorage.getItem('auth_token');
      if (token && level) {
        const res = await fetch(`${API_URL}/api/user/certificate/${level}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const blob = await res.blob();
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          const firstName = (userName || 'Utente').split(' ')[0];
          a.download = `SAPIENS_Certificato_L${level}_${firstName}.pdf`;
          a.click();
          URL.revokeObjectURL(url);
          if (onDownload) await onDownload();
          return;
        }
      }
    } catch {}
    // Fallback to client-side PDF
    generatePDF(userName, name, issuedAt, verificationCode);
    if (onDownload) await onDownload();
    setDownloading(false);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.85)' }}
          onClick={(e) => e.target === e.currentTarget && onClose()}
          data-testid="certificate-modal"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.92, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.92, y: 20 }}
            transition={{ duration: 0.25 }}
            className="w-full max-w-2xl"
            style={{ background: '#252523', borderRadius: 16, border: '1px solid rgba(255,255,255,0.08)', padding: 24, position: 'relative' }}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-[#E8E8E4]">Certificato di Completamento</h2>
              <button onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center text-[#888780] hover:text-[#E8E8E4] hover:bg-white/10 transition-colors" data-testid="cert-modal-close">
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Certificate preview — available to all users */}
            <div style={{ position: 'relative' }}>
              <CertificatePreview userName={userName} levelName={name} issuedAt={issuedAt} verificationCode={verificationCode} />
            </div>

            {/* Verification code display */}
            {verificationCode && (
              <div className="mt-4 px-4 py-2.5 rounded-xl text-center" style={{ border: '1px solid rgba(201,168,76,0.2)', background: 'rgba(201,168,76,0.05)' }}>
                <p className="text-xs text-[#888780]">Codice verifica: <span className="font-mono text-[#C9A84C] font-medium tracking-widest">{verificationCode}</span></p>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-center mt-6">
              <button
                onClick={handleDownload}
                disabled={downloading}
                className="sapiens-btn-primary flex items-center gap-2 px-6 py-2.5 text-sm disabled:opacity-60"
                data-testid="cert-download-btn"
              >
                <Download className="w-4 h-4" />
                {downloading ? 'Generazione...' : 'Scarica PDF'}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CertificateModal;
